import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchSingleItem } from '../lib/cms';

export default function About() {
  const { headerBgColor } = useLogoTheme();
  const [omData, setOmData] = useState(null);

  useEffect(() => {
    async function loadAbout() {
      const data = await fetchSingleItem('om_lenea');
      if (data) setOmData(data);
    }
    loadAbout();
  }, []);

  const heroBadge = omData?.hero_badge || 'Min Filosofi & Historie';
  const heroOverskrift = omData?.hero_overskrift || 'Rejsen mod at blive et helt menneske';
  const heroUndertekst = omData?.hero_undertekst || 'Lenea Evolinee er skabt ud fra et dybt ønske om at tilbyde et nærværende og holistisk fristed, hvor du kan genetablere kontakten til din krop og din indre sandhed.';

  const kapitelBadge = omData?.kapitel_badge || 'ET NYT KAPITEL';
  const kapitelOverskrift = omData?.kapitel_overskrift || 'Hvor hjertet taler, og dit hjerte bliver hørt';

  const missionTitel = omData?.mission_titel || 'Mission';
  const missionTekst = omData?.mission_tekst || 'At guide og støtte kvinder i at forløse traumer, slippe gamle overlevelsesmønstre og genopbygge et trygt og kærligt forhold til sig selv.';

  const visionTitel = omData?.vision_titel || 'Vision';
  const visionTekst = omData?.vision_tekst || 'Et samfund hvor kvinder står stærkt i deres egen energi, mærker deres naturlige grænser og lever i dyb overensstemmelse med deres sande væsen.';

  const filosofiTitel = omData?.filosofi_titel || 'Filosofi';
  const filosofiTekst = omData?.filosofi_tekst || 'Alt er forbundet. Når kroppen afspændes, nervesystemet falder til ro og hjertet får plads, opstår naturlig heling og forbindelse til den, du i virkeligheden er.';

  const ctaOverskrift = omData?.cta_overskrift || 'Klar til at tage første skridt?';
  const ctaTekst = omData?.cta_tekst || 'Book en uforpligtende samtale i dag, og hør mere om hvordan jeg kan tilrettelægge et forløb til dig.';
  const ctaKnap = omData?.cta_knap || 'Book samtale';

  return (
    <>
      <SEO
        title="Om Lenea Evolinee"
        description="Læs historien, missionen, visionen og filosofien bag Lenea Evolinee. Lær hvordan den helhedsorienterede tilgang hjælper kvinder med at finde hjem til sig selv."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">{heroBadge}</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            {heroOverskrift}
          </h1>
          <p className="text-base sm:text-lg text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans">
            {heroUndertekst}
          </p>
        </div>
      </section>

      {/* Historien */}
      <section className="py-20 lg:py-28 bg-white border-y border-[#E8DFD5] overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

          {/* Sektion A: ET NYT KAPITEL & Hvor hjertet taler, og dit hjerte bliver hørt */}
          <div className="space-y-6">
            <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
              {kapitelBadge}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal text-[#2E2A27] leading-snug">
              {kapitelOverskrift}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60 my-4" />

            {omData?.historie_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.historie_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Det sidste års tid har der været stille i min klinik og på sociale medier.
                </p>
                <p>
                  Det har der været en grund til.
                </p>
                <p>
                  Det har været et år, hvor jeg har arbejdet dybt med mig selv – med mine skyggesider, mit mørke, mine mønstre, mine overbevisninger, mit selvværd og ikke mindst min styrke og mit mod.
                </p>
                <p>
                  Jeg har været steder i mig selv, hvor jeg har følt mig fastlåst, handlingslammet, frustreret, vred, ked af det og ensom.
                </p>
                <p>
                  Det har til tider føltes som at stå i mudder til knæene, mens noget samtidig forsøgte at suge mig længere ned i mørket og det ubevidste.
                </p>
                <p>
                  Det har været udfordrende.
                </p>
                <p className="font-medium text-[#2E2A27]">
                  Og det har været nødvendigt.
                </p>
              </div>
            )}
          </div>

          {/* Sektion B: Noget skulle ændres & Fra Zentai til Lenea Evolinee */}
          <div>
            <div className="bg-[#FAF7F2] rounded-sm border border-[#D8CDBF] shadow-xs grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#D8CDBF]">
              <div className="p-8 space-y-4">
                <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                  {omData?.sektion_b_titel_1 || 'Noget skulle ændres'}
                </h3>
                {omData?.sektion_b_tekst_1 ? (
                  <div
                    className="space-y-4 text-base text-[#5C524A] leading-relaxed font-sans cms-richtext"
                    dangerouslySetInnerHTML={{ __html: omData.sektion_b_tekst_1 }}
                  />
                ) : (
                  <div className="space-y-4 text-base text-[#5C524A] leading-relaxed font-sans">
                    <p>
                      Jeg brugte lang tid på at tænke, bekymre mig, analysere, overveje og forsøge at finde svar.
                    </p>
                    <p>
                      Mit hoved var på overarbejde.
                    </p>
                    <p>
                      Jeg var udmattet og havde brug for at stoppe op.
                    </p>
                    <p>
                      Noget skulle slippes. Noget skulle justeres. Og noget skulle have lov til at forandre sig.
                    </p>
                    <p>
                      Det gjaldt ikke kun mig, men også min lille virksomhed.
                    </p>
                    <p>
                      Jeg lukkede min hjemmeside ned og satte mine behandlinger på pause, fordi jeg kunne mærke, at noget nyt var på vej.
                    </p>
                    <p className="font-medium text-[#2E2A27]">
                      Jeg ændrede også min virksomheds navn.
                    </p>
                  </div>
                )}
              </div>

              <div className="p-8 space-y-4">
                <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                  {omData?.sektion_b_titel_2 || 'Fra Zentai til Lenea Evolinee'}
                </h3>
                {omData?.sektion_b_tekst_2 ? (
                  <div
                    className="space-y-4 text-base text-[#5C524A] leading-relaxed font-sans cms-richtext"
                    dangerouslySetInnerHTML={{ __html: omData.sektion_b_tekst_2 }}
                  />
                ) : (
                  <div className="space-y-4 text-base text-[#5C524A] leading-relaxed font-sans">
                    <p>
                      Zentai betød meget for mig.
                    </p>
                    <p>
                      Navnet kom fra ønsket om at formidle, at vi allerede er hele, præcis som vi er, uden at skulle fikses.
                    </p>
                    <p>
                      Det er stadig en grundsten i det, jeg tror på.
                    </p>
                    <p>
                      Men jeg opdagede også noget vigtigt:
                    </p>
                    <p className="italic text-[#2E2A27]">
                      Jeg gemte mig lidt bag navnet - Zentai.
                    </p>
                    <p>
                      Det var mindre sårbart at stå bag et navn end at stå helt frem som mig selv.
                    </p>
                    <p>
                      Og nu er jeg klar til at træde frem.
                    </p>
                    <p className="font-serif text-xl text-[#98866C] font-medium pt-2">
                      Som Lenea Evolinee.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sektion C: At lære at mærke mig selv */}
          <div className="space-y-6">
            <h3 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {omData?.sektion_c_titel || 'At lære at mærke mig selv'}
            </h3>
            <div className="w-16 h-0.5 bg-[#98866C]/60 my-4" />

            {omData?.sektion_c_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.sektion_c_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Noget af det vigtigste, jeg har fået øje på, er et mønster, der har fulgt mig gennem store dele af mit liv.
                </p>
                <p>
                  Jeg har været utrolig dygtig til at tilsidesætte mig selv for at være der for andre.
                </p>
                <div className="p-4 bg-[#FAF7F2] rounded-sm border-l-4 border-l-[#98866C] text-[#2E2A27] space-y-1 font-medium my-4">
                  <p>Jeg kunne mærke, hvad andre havde brug for.</p>
                  <p>Jeg kunne hjælpe, støtte, løse og endda fikse.</p>
                </div>
                <p>
                  Men jeg glemte at mærke mig selv.
                </p>
                <p>
                  Mit eget behov, min egen grænse og min egen stemme blev ofte sat til side.
                </p>
                <p>
                  Livet har på forskellige tidspunkter tvunget mig til at stoppe op og se på det.
                </p>
                <p>
                  Jeg har været igennem perioder med depression, stress, indlæggelser og store kriser i mit privatliv og mine nære relationer.
                </p>
                <p>
                  Det rystede mit fundament og efterlod mig i perioder med en dyb følelse af ensomhed.
                </p>
                <p className="font-medium text-[#2E2A27]">
                  Men det blev også nogle af de erfaringer, der førte mig tættere på mig selv.
                </p>
              </div>
            )}
          </div>

          {/* Sektion D: Jeg må være hele mig */}
          <div className="bg-[#FAF7F2] p-8 sm:p-12 rounded-sm border border-[#D8CDBF] space-y-6 shadow-xs">
            <h3 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {omData?.sektion_d_titel || 'Jeg må være hele mig'}
            </h3>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />

            {omData?.sektion_d_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.sektion_d_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Jeg har skullet lære at mærke, acceptere og tage ansvar for alle de sider af mig, som jeg tidligere havde haft svært ved at give plads.
                </p>

                <div className="py-2 pl-4 border-l-2 border-[#D8ADA0] space-y-1 text-[#2E2A27] font-medium">
                  <p>Min sårbarhed.</p>
                  <p>Min sensitivitet.</p>
                  <p>Mit behov for at trække mig.</p>
                  <p>Mine grænser.</p>
                  <p>Min vrede og frustration.</p>
                  <p>Min længsel og min nysgerrighed.</p>
                  <p>Min spiritualitet.</p>
                </div>

                <p className="font-serif text-xl text-[#2E2A27] pt-2">
                  Alt det er også mig.
                </p>
                <p className="font-medium text-[#2E2A27]">
                  Og jeg må godt være sådan.
                </p>
                <p className="font-serif text-2xl text-[#98866C] italic font-semibold">
                  Punktum.
                </p>
                <div className="space-y-1 text-[#5C524A] pt-2">
                  <p>Jeg behøver ikke undskylde for den, jeg er.</p>
                  <p>Jeg behøver ikke være på en bestemt måde for at passe ind.</p>
                  <p>Jeg behøver ikke være perfekt eller have alle svarene.</p>
                </div>
                <p className="font-serif text-2xl text-[#2E2A27] font-medium pt-2">
                  Jeg må være mig.
                </p>
              </div>
            )}
          </div>

          {/* Sektion E: Fra hovedet til hjertet */}
          <div className="space-y-6 pt-8 border-t border-[#E8DFD5]">
            <h3 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {omData?.sektion_e_titel || 'Fra hovedet til hjertet'}
            </h3>
            <div className="w-16 h-0.5 bg-[#98866C]/60 my-4" />

            {omData?.sektion_e_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.sektion_e_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  I dag står jeg et nyt sted.
                </p>
                <p>
                  Ikke fordi jeg er færdig med at udvikle mig – det bliver jeg aldrig.
                </p>
                <p>
                  Livet er ikke statisk. Vi er hele tiden i bevægelse, og der vil altid være nye lag at opdage, mærke og forstå.
                </p>
                <p>
                  Men jeg står stærkere i mig selv.
                </p>
                <p>
                  Jeg mærker en anden form for ro.
                </p>
                <p>
                  Og jeg mærker en ild.
                </p>

                <div className="py-2 pl-4 border-l-2 border-[#98866C] space-y-1 text-[#2E2A27] font-medium">
                  <p>En bevægelse.</p>
                  <p>En længsel.</p>
                  <p className="text-[#98866C]">Et nyt kapitel, der spirer fra mit bankende hjerte.</p>
                </div>

                <p>
                  Jeg ønsker at leve mere og mere fra mit hjerte – indefra og ud.
                </p>
                <div className="space-y-1 text-[#2E2A27] font-medium">
                  <p>At være tro mod mig selv.</p>
                  <p>At være autentisk.</p>
                </div>
                <p>
                  At turde stå ved den, jeg er, uden hele tiden at bekymre mig om, hvad andre tænker, om jeg er dygtig nok, eller om jeg gør det ”rigtigt”.
                </p>
                <p>
                  Jeg har taget min gamle “jeg skal være den pæne pige og være på en bestemt måde, så andre kan lide mig”-maske af.
                </p>
                <p className="font-serif text-xl text-[#98866C] italic font-medium pt-2">
                  Og det føles befriende.
                </p>
              </div>
            )}
          </div>

          {/* Sektion F: Mit lys skal have lov til at skinne */}
          <div className="space-y-6 pt-8 border-t border-[#E8DFD5]">
            <h3 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {omData?.sektion_f_titel || 'Mit lys skal have lov til at skinne'}
            </h3>
            <div className="w-16 h-0.5 bg-[#98866C]/60 my-4" />

            {omData?.sektion_f_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.sektion_f_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Jeg tror på, at vores livserfaringer – også dem, vi helst ville have været foruden – kan føre os tættere på os selv.
                </p>
                <p>
                  Jeg tror på, at der findes et sted inde i os, hvor vi kan finde hjem.
                </p>

                <div className="py-2 pl-4 border-l-2 border-[#98866C] space-y-1 text-[#2E2A27] font-medium">
                  <p>Hjem til vores krop.</p>
                  <p>Hjem til vores hjerte.</p>
                  <p>Hjem til vores egen sandhed.</p>
                  <p className="text-[#98866C]">Hjem til den, vi egentlig er.</p>
                </div>

                <p>
                  Det er det sted, jeg selv er på vej hjem til.
                </p>
                <p>
                  Og det er også det sted, jeg ønsker at møde dig fra.
                </p>
                <p>
                  Jeg ønsker at bruge det, jeg selv har erfaret, mærket og lært, til at skabe et trygt rum, hvor du kan være dig.
                </p>

                <div className="p-6 bg-[#FAF7F2] rounded-sm border border-[#D8CDBF] space-y-1.5 text-[#2E2A27] font-medium my-4">
                  <p>Et rum uden præstation.</p>
                  <p>Et rum uden krav om at være på en bestemt måde.</p>
                  <p className="text-[#98866C]">Et rum, hvor der er plads til hele dig.</p>
                </div>

                <p>
                  Jeg vil leve fra mit hjerte og bidrage med det, jeg bærer med mig – visdom, kærlighed og lys.
                </p>
                <p>
                  Det lys, jeg bærer i mit indre, ønsker jeg at lade få lov til at sprede sig videre.
                </p>
                <p>
                  Måske til dig, der står et sted i livet og mærker noget kalde indefra.
                </p>

                <div className="space-y-1 text-[#2E2A27] font-medium py-2">
                  <p>En længsel.</p>
                  <p>En stille røst.</p>
                  <p>En fornemmelse af, at der er mere.</p>
                </div>

                <p>
                  En længsel efter at komme hjem.
                </p>

                <div className="py-2 pl-4 border-l-2 border-[#98866C] space-y-1 font-serif text-lg text-[#98866C]">
                  <p>Hjem til dig.</p>
                  <p>Hjem til dit lys.</p>
                  <p>Hjem til din kerne.</p>
                </div>
              </div>
            )}
          </div>

          {/* Sektion G: Velkommen til Lenea Evolinee et nyt kapitel fra hjertet. */}
          <div className="p-8 sm:p-12 bg-[#FAF7F2] rounded-sm border border-[#D8CDBF] space-y-6 shadow-xs">
            <h3 className="font-serif text-2xl sm:text-3xl font-normal text-[#2E2A27]">
              {omData?.sektion_g_titel || 'Velkommen til Lenea Evolinee et nyt kapitel fra hjertet.'}
            </h3>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />

            {omData?.sektion_g_tekst ? (
              <div
                className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                dangerouslySetInnerHTML={{ __html: omData.sektion_g_tekst }}
              />
            ) : (
              <div className="space-y-4 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Et sted, hvor jeg ikke længere gemmer mig bag et navn.
                </p>
                <p>
                  Hvor jeg står frem som mig.
                </p>
                <p>
                  Med mit hjerte, min erfaring, min sårbarhed, min styrke og alt det, jeg er.
                </p>
                <p className="font-serif text-xl sm:text-2xl text-[#2E2A27] font-medium pt-2">
                  Hvor hjertet taler – og dit hjerte bliver hørt.
                </p>

                <div className="pt-6 font-serif text-xl text-[#2E2A27]">
                  <p>Med kærlighed og åbent hjerte</p>
                  <p className="italic text-[#98866C] pt-1 font-medium">Lenea</p>
                </div>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* Mission, Vision & Filosofi */}
      <section className="py-24 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-sm border border-[#D8CDBF] space-y-4 shadow-xs">
              <div className="w-12 h-12 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center text-xl">
                <i className="fa-solid fa-compass" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                {missionTitel}
              </h3>
              <p className="text-sm text-[#5C524A] leading-relaxed font-sans">
                {missionTekst}
              </p>
            </div>

            <div className="bg-white p-8 rounded-sm border border-[#D8CDBF] border-t-2 border-t-[#D8ADA0] space-y-4 shadow-xs">
              <div className="w-12 h-12 rounded-full bg-[#F3E6E2] text-[#B26F5E] flex items-center justify-center text-xl">
                <i className="fa-solid fa-eye" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                {visionTitel}
              </h3>
              <p className="text-sm text-[#5C524A] leading-relaxed font-sans">
                {visionTekst}
              </p>
            </div>

            <div className="bg-white p-8 rounded-sm border border-[#D8CDBF] space-y-4 shadow-xs md:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 rounded-full bg-[#98866C]/20 text-[#98866C] flex items-center justify-center text-xl">
                <i className="fa-solid fa-yin-yang" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl font-normal text-[#2E2A27]">
                {filosofiTitel}
              </h3>
              <p className="text-sm text-[#5C524A] leading-relaxed font-sans">
                {filosofiTekst}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-white border-t border-[#E8DFD5] text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">Start din rejse</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27] -mt-2">
            {ctaOverskrift}
          </h2>
          <p className="text-base text-[#5C524A] font-sans">
            {ctaTekst}
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
            >
              <span>{ctaKnap}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
